import {
  RedeemTxType,
  GoldWithdrawFilters,
  GoldWithdrawFilterKey,
  GoldWithdrawFilterValue,
} from 'src/type/gold-withdraw';
import { Card, Stack, Table, TableBody, TableContainer } from '@mui/material';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { isAfter } from 'src/utils/format-time';
import Scrollbar from 'src/components/scrollbar';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { useBoolean } from 'src/hooks/use-boolean';
import usePagination from 'src/hooks/use-pagination';
import axios, { endpoints } from 'src/utils/axios';
import { fParams, fDateRange, ParamsType } from 'src/utils/format-params';
import { Pagination } from 'src/type/utils';
import { toastError } from 'src/utils/format-error';
import { TransactionType, TransactionStatus, TransactionBadgeType } from 'src/type/transaction';
import TableLoading from 'src/components/table/table-loading';
import { emptyRows } from 'src/components/table/utils';

import useProductOptions from 'src/hooks/use-product-options';
import { UserType } from 'src/type/user';
import StatusTabFilter, { StatusTabFilterOption } from 'src/components/ui-kit/status-tab-filter';
import LoadingDialog from 'src/components/ui-kit/loading-dialog';
import { enqueueSnackbar } from 'notistack';
import RedeemTableFiltersResult from './redeem-table-filters-result';
import RedeemTableRow from './redeem-table-row';
import RedeemTableToolbar from './redeem-table-toolbar';
import RedeemCreateDialog from './redeem-create-dialog';

const defaultFilters: GoldWithdrawFilters = {
  name: null,
  productId: 'all',
  startDate: null,
  endDate: null,
  status: 'all',
};

type Props = {
  user?: UserType;
  enableCreate?: boolean;
  isCreateAndApprove?: boolean;
  rootReload?: () => void;
};

const tableHead = [
  { id: 'ลูกค้า', label: 'ลูกค้า' },
  { id: 'product', label: 'สินค้า', width: 140 },
  { id: 'sent', label: 'น้ำหนัก', width: 120 },
  { id: 'createDate', label: 'วันที่ทำรายการ', width: 180 },
  // { id: 'dueDate', label: 'วันที่อนุมัติ', width: 160 },
  { id: 'status', label: 'สถานะ', width: 120 },
  { id: 'action', width: 60 },
];

export default function RedeemTableView({
  user,
  enableCreate = false,
  isCreateAndApprove = false,
  rootReload,
}: Props) {
  const initialize = useBoolean(true);
  const loading = useBoolean();
  // const approve = useBoolean();
  const [filters, setFilters] = useState(defaultFilters);

  const { productOptions } = useProductOptions(false, user?.uid);

  // const [singleTx, setSingleTx] = useState<RedeemTxType | null>(null);

  const [badge, setBadge] = useState<TransactionBadgeType>({
    total: 0,
    waiting: 0,
    pending: 0,
    success: 0,
    rejected: 0,
    cancel: 0,
    failed: 0,
  });

  const dateError = isAfter(filters.startDate?.toDate(), filters.endDate?.toDate());

  const form = useBoolean(false);
  const reload = useBoolean(false);

  const canReset =
    !!filters.name ||
    filters.productId !== 'all' ||
    filters.status !== 'all' ||
    (!!filters.startDate && !!filters.endDate);

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<RedeemTxType>();

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      ...fDateRange(filters.startDate, filters.endDate),

      type: TransactionType.REDEEM,
      productId: filters.productId === 'all' ? undefined : filters.productId,
      status: filters.status === 'all' ? undefined : filters.status,
      page: currentPage,
      limit: rowsPerPage,
      userId: user?.uid || filters.name?.value,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    handleGetInviteCode(params);
  }, [
    currentPage,
    rowsPerPage,
    filters.startDate,
    filters.endDate,
    filters.status,
    filters.productId,
    reload.value,
    filters.name,
  ]);

  const handleGetInviteCode = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();
      const { data: _badge } = await axios.get<TransactionBadgeType>(
        endpoints.transactions.badges,
        {
          params: {
            type: TransactionType.REDEEM,
          },
        }
      );
      setBadge(_badge);
      const { data } = await axios.get<Pagination<RedeemTxType>>(endpoints.transactions.get, {
        params: fParams(params),
      });
      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  // const handleGetTransactionById = useCallback(async (id: string) => {
  //   try {
  //     loading.onTrue();

  //     const { data } = await axios.get<RedeemTxType>(endpoints.transactions.getById(id));
  //     setSingleTx(data);
  //   } catch (error) {
  //     toastError(error);
  //   } finally {
  //     loading.onFalse();
  //   }
  // }, []);

  const handleFilters = useCallback(
    (name: GoldWithdrawFilterKey, value: GoldWithdrawFilterValue) => {
      onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    onResetPage();
    setFilters(defaultFilters);
  }, []);

  const handleReload = useCallback(() => {
    onResetPage();
    if (rootReload) {
      rootReload();
    }
    reload.onToggle();
  }, []);

  const tabs: StatusTabFilterOption = useMemo(
    () => [
      { value: 'all', label: 'ทั้งหมด', color: 'default', count: badge.total },
      {
        value: TransactionStatus.SUCCESS,
        label: 'สำเร็จ',
        color: 'success',
        count: badge.success,
      },
      {
        value: `${TransactionStatus.PENDING},${TransactionStatus.WAITING_TICKET}`,
        label: 'รอดำเนินการ',
        color: 'warning',
        count: badge.pending + (badge?.waitingTicket || 0),
      },
      {
        value: TransactionStatus.REJECTED,
        label: 'ปฏิเสธ',
        color: 'error',
        count: badge.rejected,
      },
      // {
      //   value: TransactionStatus.CANCEL,
      //   label: 'ยกเลิก',
      //   color: 'info',
      //   count: badge.cancel,
      // },
    ],
    [badge]
  );

  return (
    <>
      <Stack spacing={3}>
        <Card>
          {!user && (
            <StatusTabFilter
              value={filters.status}
              onChange={(newValue) => handleFilters('status', newValue)}
              options={tabs}
            />
          )}

          {/* <Stack direction="row" justifyContent="space-between" alignItems="center" pr={2.5}> */}
          <RedeemTableToolbar
            filters={filters}
            onFilters={handleFilters}
            dateError={dateError}
            productOptions={productOptions}
            disabledSearch={!!user}
            enableCreate={enableCreate || !!user}
            onCreateRedeem={form.onTrue}
          />

          {/* {user && (
              <Button
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
                onClick={form.onTrue}
                sx={{ width: '100%', maxWidth: 200 }}
              >
                สร้างรายการถอนสินค้า
              </Button>
            )}
          </Stack> */}

          {canReset && (
            <RedeemTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={meta.totalResults}
              sx={{ p: 2.5, pt: 0 }}
              productOptions={productOptions}
            />
          )}

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={rowsPerPage}>
                    {tableData.map((row) => (
                      <RedeemTableRow key={row.id} row={row} handleReload={handleReload} />
                    ))}

                    <TableEmptyRows
                      show={!initialize.value}
                      height={76}
                      emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                    />

                    <TableNoData notFound={!tableData.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={meta.totalResults}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Card>
      </Stack>
      {!!form.value && (!!enableCreate || !!user) && (
        <RedeemCreateDialog
          open={form.value}
          onClose={form.onFalse}
          user={enableCreate && !user ? null : user || null}
          handleReload={handleReload}
          isCreateAndApprove={isCreateAndApprove}
          openApproveDialog={(txId) => {
            (async () => {
              enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
              handleReload();

              // if (txId) {
              //   await handleGetTransactionById(txId);
              //   approve.onTrue();
              // }
            })();
          }}
        />
      )}

      {/* {!!approve.value && !!singleTx && (
        <RedeemApproveDialog
          isCreateAndApprove={isCreateAndApprove}
          open={approve.value}
          onClose={approve.onFalse}
          handleReload={handleReload}
          tx={singleTx}
        />
      )} */}

      <LoadingDialog open={loading.value} />
    </>
  );
}
